import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBolt } from '@fortawesome/free-solid-svg-icons';
import { VisionModalService } from '../vision-modal/vision-modal.service';
import type { PillContent } from './product-pill-content';
import { ProductPillService } from './product-pill.service';

/**
 * Renders pill badges on product-card surfaces based on the `productPills`
 * multiselect set on the Craft Product entry. Each entry in PRODUCT_PILL_CONTENT
 * is one possible pill — clicking it opens a global modal with that pill's copy.
 *
 * Adding a new pill type:
 *   1. Add the value to the Craft `productPills` multiselect options.
 *   2. Add an entry to PRODUCT_PILL_CONTENT with label, modal copy, and the
 *      class names that style the pill, modal frame, and card border.
 *   3. Add brand-skinnable CSS for those classes in _product-pills.scss.
 */
@Component({
  selector: 'product-pill',
  templateUrl: './product-pill.component.html',
  standalone: true,
  imports: [FontAwesomeModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductPillComponent {
  @Input() pills: string[] | null | undefined = [];

  readonly faBolt = faBolt;

  private readonly pillService = inject(ProductPillService);
  private readonly modalService = inject(VisionModalService);

  get visiblePills(): Array<{ key: string; content: PillContent }> {
    return this.pillService.getVisiblePills(this.pills);
  }

  onPillClick(event: MouseEvent, content: PillContent): void {
    // Non-interactive pills (e.g. DEC's Smart Practice badge) do nothing when activated. Guard on
    // every field the modal actually renders (header + title + body) so a pill missing any of them
    // never opens a modal that would show literal "undefined" strings. This also covers keyboard
    // activation, which CSS `pointer-events: none` (mouse-only) can't. Return early *before*
    // touching the event so activating a non-modal pill is a no-op.
    if (!content.modalHeader || !content.modalTitle || !content.modalBody) {
      return;
    }
    // Pills can be nested inside anchor cards (popular-courses, new-card-item).
    // Stop propagation + prevent default so clicking the pill opens the modal
    // without also triggering the card link navigation.
    event.stopPropagation();
    event.preventDefault();
    this.openPillModal(content);
  }

  openPillModal(content: PillContent): void {
    this.modalService.openModal({
      showModal: true,
      modalContent: this.buildModalContent(content),
      bodyClass: content.modalBodyClass,
    });
  }

  private buildModalContent(content: PillContent): string {
    // VisionModal renders this via [innerHTML], which Angular sanitizes — inline <svg>
    // would be stripped, so the bolt icon is drawn in CSS on a plain (sanitizer-safe)
    // <span> (.product-pill-modal__header-icon), mirroring the pill button's bolt.
    return `
      <div class="product-pill-modal__header">
        <span class="product-pill-modal__header-icon" aria-hidden="true"></span>
        <span>${content.modalHeader}</span>
      </div>
      <h3 class="product-pill-modal__title">${content.modalTitle}</h3>
      <p class="product-pill-modal__body">${content.modalBody}</p>
    `;
  }
}
